import Core from './src/core/Core';
import CdnBalancer from './src/balancer/CdnBalancer';
import BalancerAnalytics from './src/balancer/video/BalancerAnalytics';
import Log, { LogLevel } from './src/common/log';
import NpawPluginOptions from './src/core/NpawPluginOptions';
import { CDNDefaultSettings } from './src/balancer/Utils/Constants';
import CustomOptionsProcessor from './src/balancer/Utils/CustomOptionsProcessor';
global.LogLevel = LogLevel;
global.NpawPluginOptions = NpawPluginOptions;
export { LogLevel } from './src/common/log';
export * from './src/core/NpawPluginOptions';
export default class CdnBalancerPlugin {
    constructor(accountCode, options) {
        this.accountCode = accountCode;
        this.core = Core.getInstance(accountCode, options);
        this.video = this.createVideoAnalyticsIfEnabled((options === null || options === void 0 ? void 0 : options.balancerAnalyticsEnabled) === true);
        this.cdnBalancer = new CdnBalancer(accountCode, options, this.video);
    }
    destroy() {
        var _a;
        (_a = this.video) === null || _a === void 0 ? void 0 : _a.destroy();
        this.video = undefined;
        this.core.destroy();
        this.cdnBalancer.destroy();
    }
    /**
     * Changes the log level of the NPAW Plugin. See {@link LogLevel} for the different levels available
     *
     * @param logLevel Log level to be used
     */
    setLogLevel(logLevel) {
        Log.setLogLevel(logLevel);
    }
    /**
     * Sets Balancer options. See {@link BalancerOptions}.
     * @param options Balancer options
     */
    setBalancerOptions(options) {
        this.cdnBalancer.setOptions(options);
        const newVideo = this.createVideoAnalyticsIfEnabled(options.balancerAnalyticsEnabled === true);
        if (this.video && this.video !== newVideo) {
            this.video.destroy();
        }
        this.video = newVideo;
        this.cdnBalancer.setVideoLogic(this.video);
    }
    /**
     * Sets the manifest URL of the played content in the Balancer options object.
     * For url changes the loader is restarted.
     * @param url Manifest url of the content being played.
     * @public
     */
    setManifestResource(url) {
        this.cdnBalancer.setManifestResource(url);
    }
    /**
     * Adds an interceptor to the Balancer
     * @param name Name of the interceptor
     * @param player Video player object
     */
    addInterceptor(name, player) {
        this.cdnBalancer.addInterceptor(name, player);
    }
    /**
     * Returns Bitmovin configuration for request interception
     */
    getBitmovinSendHttpRequest() {
        return this.cdnBalancer.getBitmovinSendHttpRequest();
    }
    /**
     * Returns the manifest/video URL to be used following the Selector API.
     * If an error occurs this will return undefined
     */
    getRecommendedManifest() {
        return this.cdnBalancer.getRecommendedManifest();
    }
    /**
     * @internal
     * @returns True if balancer is enabled, false otherwise
     */
    isBalancerEnabled() {
        return this.cdnBalancer.getIsEnabled();
    }
    getManifestResolutionStats() {
        return this.cdnBalancer.getManifestResolutionStats();
    }
    /**
     * Merge runtime provider hosts on top of the latest /decision baseline.
     * See {@link ExternalProvidersOptions} for merge modes.
     */
    setExternalProviders(providers, options) {
        this.cdnBalancer.setExternalProviders(providers, options);
    }
    /**
     * Refresh the runtime provider overlay mid-session.
     */
    updateExternalProviders(providers, options) {
        this.cdnBalancer.updateExternalProviders(providers, options);
    }
    /**
     * Remove the runtime provider overlay and restore /decision baseline providers.
     */
    clearExternalProviders() {
        this.cdnBalancer.clearExternalProviders();
    }
    /**
     * Snapshot of the configured runtime provider overlay.
     */
    getExternalProviders() {
        return this.cdnBalancer.getExternalProviders();
    }
    appendExternalProviders(providers) {
        this.cdnBalancer.appendExternalProviders(providers);
    }
    prependExternalProviders(providers) {
        this.cdnBalancer.prependExternalProviders(providers);
    }
    insertExternalProviders(providers, index) {
        this.cdnBalancer.insertExternalProviders(providers, index);
    }
    setLegacyPlugin(legacyPlugin) {
        Core.getInstance().setLegacyPlugin(legacyPlugin);
    }
    /**
     * Apply a customData.options-shaped bag to the live BalancerOptions. Same
     * dotted-key processor the /decision response uses, so dashboards / sample
     * panels can drive the four-timeout / latency-trial / peak-sampler /
     * first-chunk-probe knobs without round-tripping through the backend.
     */
    applyCustomData(customDataOptions) {
        CustomOptionsProcessor.applyCustomOptionsToBalancerOptions(this.cdnBalancer.options, customDataOptions);
    }
    /**
     * Snapshot of the active four-timeout values (connect / read / call / total,
     * all in milliseconds). A value of 0 or below means "no deadline" and the
     * corresponding timer is not armed.
     */
    static getCdnTimeouts() {
        return {
            connectTimeoutMilliseconds: CDNDefaultSettings.connectTimeoutMilliseconds,
            readTimeoutMilliseconds: CDNDefaultSettings.readTimeoutMilliseconds,
            callTimeoutMilliseconds: CDNDefaultSettings.callTimeoutMilliseconds,
            totalTimeoutMilliseconds: CDNDefaultSettings.totalTimeoutMilliseconds
        };
    }
    /**
     * Mutate the active four-timeout values. Only the supplied keys are touched
     * (others stay as-is). `requestTimeoutMilliseconds` is kept in sync with
     * `callTimeoutMilliseconds` for legacy callers. Pass <= 0 to disable a timer.
     */
    static setCdnTimeouts(timeouts) {
        if (typeof timeouts.connectTimeoutMilliseconds === 'number') {
            CDNDefaultSettings.connectTimeoutMilliseconds = timeouts.connectTimeoutMilliseconds;
        }
        if (typeof timeouts.readTimeoutMilliseconds === 'number') {
            CDNDefaultSettings.readTimeoutMilliseconds = timeouts.readTimeoutMilliseconds;
        }
        if (typeof timeouts.callTimeoutMilliseconds === 'number') {
            CDNDefaultSettings.callTimeoutMilliseconds = timeouts.callTimeoutMilliseconds;
            CDNDefaultSettings.requestTimeoutMilliseconds = timeouts.callTimeoutMilliseconds;
        }
        if (typeof timeouts.totalTimeoutMilliseconds === 'number') {
            CDNDefaultSettings.totalTimeoutMilliseconds = timeouts.totalTimeoutMilliseconds;
        }
        return CdnBalancerPlugin.getCdnTimeouts();
    }
    createVideoAnalyticsIfEnabled(enabled) {
        if (!enabled) {
            return undefined;
        }
        if (this.video) {
            return this.video;
        }
        return new BalancerAnalytics(this.core, this.accountCode);
    }
}
